import { ResendNotificationModal } from '../../../ui/components/higher-order/resend-notification-action/resend-notification-modal.component';
import { DeliveryDetailsActions } from './actions/deliveries-details.actions';
import { useDeliveryDetails } from './hooks/use-delivery-details.hook';
import { Snackbar } from '../../../ui/components/pure/snackbar/snackbar.component';
import { MarkCollectedModal } from './actions/mark-collected/mark-collected-action.component';
import { MarkCancelledModal } from './actions/mark-cancelled/mark-cancelled-action.component';
import { MarkRetrievedModal } from './actions/mark-retrieved/mark-retrieved-action.component';
import { OpenLockerModal } from './actions/open-locker/open-locker-modal.component';
import { Delivery } from '../models/delivery.model';
import { AddNoteActionModal } from '../../../ui/components/higher-order/add-note-action/add-note-modal.component';
import { KnownNoteEntityTypes } from '../../../ui/components/higher-order/add-note-action/add-note.hook';
import { SwitchSpaceModal } from './actions/switch-space/switch-space-action.component';
import { ArchiveDeliveryModal } from './actions/archive-delivery/archive-delivery-action.component';
import { ForceUpdateModal } from './actions/force-update/force-update-action.component';
import { Paths, createPath } from '../../../app/router/app.router';
import { useHistory } from 'react-router-dom';
import { LocationState } from '../../../ui/components/pure/list-table/list-table.types';
import { getEnvironment } from '../../../core/env/environment';

export function DeliveryDetailsActionsObserver({
  delivery,
  state,
  handlers,
  projectID,
  locationState
}: {
  delivery: Delivery;
  state: ReturnType<typeof useDeliveryDetails>['state'];
  handlers: ReturnType<typeof useDeliveryDetails>['handlers'];
  projectID: string;
  locationState: LocationState;
}) {
  const history = useHistory();
  const url = createPath(Paths.deliveries, { projectID: projectID });

  switch (state.currentAction) {
    case DeliveryDetailsActions.ADD_NOTE:
      return (
        <AddNoteActionModal
          id={delivery.id}
          projectID={delivery.project.id}
          entityType={KnownNoteEntityTypes.Delivery}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(DeliveryDetailsActions.ADD_NOTE_SUCCESS)}
        />
      );
    case DeliveryDetailsActions.ADD_NOTE_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          Note added
        </Snackbar>
      );
    case DeliveryDetailsActions.RESEND_NOTIFICATION:
      return (
        <ResendNotificationModal
          delivery={delivery}
          onSuccess={() => handlers.endAction(DeliveryDetailsActions.RESEND_NOTIFICATION_SUCCESS)}
          onAbort={() => handlers.endAction()}
        />
      );
    case DeliveryDetailsActions.RESEND_NOTIFICATION_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          A notification resend request has been sent. The status of the notification will be available in the timeline
          shortly.
        </Snackbar>
      );
    case DeliveryDetailsActions.FORCE_UPDATE:
      return (
        <ForceUpdateModal
          delivery={delivery}
          onAbort={() => handlers.endAction()}
          onSuccess={() => handlers.endAction(DeliveryDetailsActions.FORCE_UPDATE_SUCCESS)}
        />
      );
    case DeliveryDetailsActions.FORCE_UPDATE_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The delivery state has been updated
        </Snackbar>
      );
    case DeliveryDetailsActions.MARK_COLLECTED:
      return (
        <MarkCollectedModal
          delivery={delivery}
          onAbort={() => handlers.endAction()}
          onFailed={() => handlers.endAction(DeliveryDetailsActions.MARK_COLLECTED_FAILED)}
          onSuccess={() => handlers.endAction(DeliveryDetailsActions.MARK_COLLECTED_SUCCESS)}
        />
      );
    case DeliveryDetailsActions.MARK_COLLECTED_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The delivery has been marked as Collected
        </Snackbar>
      );
    case DeliveryDetailsActions.MARK_COLLECTED_FAILED:
      return (
        <Snackbar severity="error" onClose={() => handlers.endAction()}>
          We can’t confirm the update on the lockers right now. Please check back shortly and you’ll see the delivery
          updated.
        </Snackbar>
      );
    case DeliveryDetailsActions.MARK_CANCELLED:
      return (
        <MarkCancelledModal
          delivery={delivery}
          onAbort={() => handlers.endAction()}
          onFailed={() => handlers.endAction(DeliveryDetailsActions.MARK_CANCELLED_FAILED)}
          onSuccess={() => handlers.endAction(DeliveryDetailsActions.MARK_CANCELLED_SUCCESS)}
        />
      );
    case DeliveryDetailsActions.MARK_CANCELLED_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The delivery has been marked as {delivery.stateLabel}
        </Snackbar>
      );
    case DeliveryDetailsActions.MARK_CANCELLED_FAILED:
      return (
        <Snackbar severity="error" onClose={() => handlers.endAction()}>
          We can’t confirm the update on the lockers right now. Please check back shortly and you’ll see the delivery
          updated.
        </Snackbar>
      );
    case DeliveryDetailsActions.MARK_RETRIEVED:
      return (
        <MarkRetrievedModal
          delivery={delivery}
          onAbort={() => handlers.endAction()}
          onFailed={() => handlers.endAction(DeliveryDetailsActions.MARK_RETRIEVED_FAILED)}
          onSuccess={() => handlers.endAction(DeliveryDetailsActions.MARK_RETRIEVED_SUCCESS)}
        />
      );
    case DeliveryDetailsActions.MARK_RETRIEVED_FAILED:
      return (
        <Snackbar severity="error" onClose={() => handlers.endAction()}>
          We can’t confirm the update on the lockers right now. Please check back shortly and you’ll see the delivery
          updated.
        </Snackbar>
      );
    case DeliveryDetailsActions.MARK_RETRIEVED_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The delivery has been marked as {delivery.stateLabel}
        </Snackbar>
      );
    case DeliveryDetailsActions.OPEN_LOCKER:
      return (
        <OpenLockerModal
          delivery={delivery}
          onSuccess={(statusChanged: boolean) => {
            handlers.endAction(statusChanged ? DeliveryDetailsActions.OPEN_LOCKER_SUCCESS : undefined);
          }}
          onAbort={() => handlers.endAction()}
        />
      );
    case DeliveryDetailsActions.OPEN_LOCKER_SUCCESS:
      return (
        <Snackbar severity="success" onClose={() => handlers.endAction()}>
          The delivery has been marked {delivery.stateLabel}
        </Snackbar>
      );
    case DeliveryDetailsActions.OPEN_LINK_TO_AC_DELIVERY:
      window.open(generateAcDeliveryDetailsUrl(delivery));
      handlers.endAction();
      return null;
    case DeliveryDetailsActions.SWITCH_SPACE:
      if (!delivery.deliveredInto?.hub) {
        return null;
      }
      return (
        <SwitchSpaceModal
          deliveryID={delivery.id}
          hubID={delivery.deliveredInto.hub.id}
          onAbort={() => handlers.endAction()}
        />
      );
    case DeliveryDetailsActions.ARCHIVE_DELIVERY:
      return (
        <ArchiveDeliveryModal
          delivery={delivery}
          onAbort={() => handlers.endAction()}
          onSuccess={() => {
            handlers.endAction();
            history.push(`${url}${locationState?.from?.search || ''}`);
          }}
        />
      );
    default:
      return null;
  }
}

export function generateAcDeliveryDetailsUrl(delivery: Delivery): string | undefined {
  const baseUrl = getEnvironment().acUrl;
  const migratedFromAcDeliveryId = delivery.migratedFromAcDeliveryId;
  if (!migratedFromAcDeliveryId) return undefined;
  return `${baseUrl}frmParcelRecord.aspx?a=0&?b=${migratedFromAcDeliveryId}&?c=&?d=&?e=&?f=&?g=&?h=&?i=0&?j=&?k=&?l=&?m=`;
}
